import axios, { AxiosInstance, AxiosResponse } from 'axios';
import type { PlatformAdapter } from './platform';
import { isSessionExpiredError, type EnhancedError, type RateLimitPayload } from './errors';

/**
 * Centralized token refresh manager.
 * Ensures only ONE refresh request happens at a time across the entire application.
 * Uses the shared-promise pattern to prevent concurrent refresh calls.
 */

interface RefreshResponse {
  accessToken: string;
  refreshToken?: string;
  message?: string;
  error?: string;
  detail?: string;
  code?: string;
}

type RefreshEnhancedError = Error & {
  status?: number;
  code?: string;
  response?: AxiosResponse<RefreshResponse>;
};

const isRefreshEnhancedError = (error: unknown): error is RefreshEnhancedError =>
  typeof error === 'object' && error !== null && 'status' in error;

/** State for the refresh manager — scoped per client instance */
interface RefreshState {
  isRefreshing: boolean;
  refreshPromise: Promise<string> | null;
  isHandlingSessionExpiry: boolean;
  sessionExpired: boolean;
}

function createRefreshState(): RefreshState {
  return {
    isRefreshing: false,
    refreshPromise: null,
    isHandlingSessionExpiry: false,
    sessionExpired: false,
  };
}

/**
 * Perform a token refresh using the adapter's refresh token.
 * Multiple simultaneous calls will share the same in-flight promise.
 */
function refreshAccessToken(adapter: PlatformAdapter, state: RefreshState): Promise<string> {
  if (state.isRefreshing && state.refreshPromise) {
    return state.refreshPromise;
  }

  state.isRefreshing = true;

  state.refreshPromise = (async () => {
    try {
      const refreshToken = await adapter.getRefreshToken();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const baseURL = adapter.getApiBaseUrl();

      const response = await axios.post<RefreshResponse>(
        '/auth/refresh',
        { refreshToken },
        {
          baseURL,
          withCredentials: true,
          headers: { 'Content-Type': 'application/json' },
          validateStatus: status => status < 500,
        }
      );

      if (response.status !== 200) {
        const error: RefreshEnhancedError = new Error(
          response.data?.message ||
            response.data?.error ||
            response.data?.detail ||
            'Token refresh failed'
        );
        error.status = response.status;
        error.code = response.data?.code;
        error.response = response;
        throw error;
      }

      const { accessToken, refreshToken: newRefreshToken } = response.data;

      // Store the new tokens
      await adapter.setAccessToken(accessToken);
      if (newRefreshToken) {
        await adapter.setRefreshToken(newRefreshToken);
      }

      state.isRefreshing = false;
      state.refreshPromise = null;

      return accessToken;
    } catch (error) {
      state.isRefreshing = false;
      state.refreshPromise = null;

      if (isRefreshEnhancedError(error)) {
        throw error;
      }

      if (axios.isAxiosError(error)) {
        const enhancedError: RefreshEnhancedError = new Error(
          error.response?.data?.message || error.message || 'Token refresh failed'
        );
        enhancedError.status = error.response?.status;
        enhancedError.code =
          typeof error.response?.data?.code === 'string' ? error.response?.data?.code : undefined;
        enhancedError.response = error.response;
        throw enhancedError;
      }

      throw error;
    }
  })();

  return state.refreshPromise;
}

/**
 * Install request + response interceptors on an Axios instance.
 *
 * - Request: attaches Bearer token from adapter (skips auth endpoints)
 * - Response 401: refreshes token via adapter and retries once
 * - Response 429: enhances error with retry-after metadata
 * - Response 440: delegates to adapter.onSessionExpired()
 */
export function installInterceptors(client: AxiosInstance, adapter: PlatformAdapter): void {
  const state = createRefreshState();

  // --- Request interceptor ---
  client.interceptors.request.use(
    async config => {
      // Normalize trailing slashes on URLs
      if (config.url) {
        config.url = normalizeUrl(config.url);
      }

      // Don't add token to auth endpoints
      const authEndpoints = ['/auth/login', '/auth/setup', '/auth/refresh', '/auth/status'];
      const isAuthEndpoint = authEndpoints.some(ep => config.url?.includes(ep));

      if (!isAuthEndpoint) {
        const token = await adapter.getAccessToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    error => Promise.reject(error)
  );

  // --- Response interceptor ---
  client.interceptors.response.use(
    response => response,
    async error => {
      if (!axios.isAxiosError(error)) {
        return Promise.reject(error);
      }

      const originalRequest = error.config as typeof error.config & { _retry?: boolean };

      // 429 — Rate limited
      if (error.response?.status === 429) {
        const errorData = error.response?.data as RateLimitPayload | undefined;
        const retryAfterHeader = error.response?.headers?.['retry-after'];
        const retryAfter = errorData?.retryAfter ?? retryAfterHeader;
        const message = errorData?.message ?? 'Too many requests. Please try again later.';

        if (adapter.isDevelopment()) {
          console.warn(
            `[api-client] Rate limited (429) on ${originalRequest?.url}. Retry after: ${retryAfter}s`
          );
        }

        const enhancedError: EnhancedError = new Error(message);
        enhancedError.status = 429;
        enhancedError.retryAfter = retryAfter;
        enhancedError.response = error.response;
        return Promise.reject(enhancedError);
      }

      // 440 — Session expired
      if (error.response?.status === 440) {
        if (!state.isHandlingSessionExpiry) {
          state.isHandlingSessionExpiry = true;
          state.sessionExpired = true;
          console.log('[api-client] Session expired (440)');
          await adapter.onSessionExpired();
        }
        return Promise.reject(error);
      }

      // 401 — Attempt token refresh + retry
      if (
        error.response?.status === 401 &&
        originalRequest &&
        !originalRequest._retry &&
        !originalRequest.url?.includes('/auth/refresh') &&
        !state.isHandlingSessionExpiry &&
        !state.sessionExpired
      ) {
        originalRequest._retry = true;

        try {
          const newAccessToken = await refreshAccessToken(adapter, state);

          if (!newAccessToken) {
            if (!state.isHandlingSessionExpiry) {
              state.isHandlingSessionExpiry = true;
              state.sessionExpired = true;
              await adapter.onSessionExpired();
            }
            return Promise.reject(new Error('Token refresh failed'));
          }

          originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
          return client(originalRequest);
        } catch (refreshError) {
          if (!state.isHandlingSessionExpiry) {
            state.isHandlingSessionExpiry = true;
            state.sessionExpired = true;
            const reason = isSessionExpiredError(refreshError)
              ? 'session-expired response'
              : refreshError instanceof Error
                ? refreshError.message
                : 'unknown error';
            console.log(`[api-client] Token refresh failed (${reason}); logging out`);
            await adapter.onSessionExpired();
          }
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );
}

/**
 * Normalize a URL by stripping trailing slashes from the path component.
 */
export function normalizeUrl(url: string | undefined): string | undefined {
  if (!url) return url;

  try {
    const parsed = new URL(url);
    if (parsed.pathname.length > 1 && parsed.pathname.endsWith('/')) {
      parsed.pathname = parsed.pathname.slice(0, -1);
    }
    return parsed.toString();
  } catch {
    const match = url.match(/^([^?#]*)(.*)$/);
    if (!match) return url;

    const [, path, suffix] = match;
    if (path.length > 1 && path.endsWith('/')) {
      return `${path.slice(0, -1)}${suffix}`;
    }
    return url;
  }
}
